import {Injectable} from '@angular/core';
import {InvoiceAddressState, Store as InvoiceAddressStore} from './store';
import {EntityService} from '../../../components/entity-service';
import {InvoiceAddress} from './model';
import {FieldConfig} from '../../../components/form/field.interface';
import {Validators} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {map, shareReplay, switchMap, take} from 'rxjs/operators';
import {InvoiceAddressQuery} from './query';
import {Observable, of, Subject} from "rxjs";
import {AuthService} from "../../../authentication/service/auth.service";
import {User} from "../../../authentication/user/state/model";

@Injectable({providedIn: 'root'})
export class InvoiceAddressService extends EntityService<InvoiceAddressStore, InvoiceAddressState> {

  private addressListChanged = new Subject<void>();
  addressListChanged$ = this.addressListChanged.asObservable();
  private pendingCheckoutAddress: InvoiceAddress | null = null;

  addresses$ = this.authService.user$.pipe(
    switchMap(() => this.get()),
    map(items => items.map(item => new InvoiceAddress(item))),
    shareReplay(1)
  );

  editForm = {
    cancelTitle: this.translocoService.selectTranslate('form.cancel'),
    submitTitle: this.translocoService.selectTranslate('form.submit'),
    entityTitle: this.translocoService.selectTranslate('country.entityTitle'),
    fields: [

      FieldConfig.create({
        type: 'input',
        label: of(''),
        inputType: 'text',
        name: 'address',
        listed: true,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message$: this.translocoService.selectTranslate('form.required')
          }
        ]
      }),
      FieldConfig.create({
        type: 'input',
        label: of(''),
        inputType: 'text',
        name: 'user',
        listed: false,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message$: this.translocoService.selectTranslate('form.required')
          }
        ]
      }),
      FieldConfig.create({
        type: 'input',
        label: of(''),
        inputType: 'text',
        name: 'country',
        listed: true,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message$: this.translocoService.selectTranslate('form.required')
          }
        ]
      }),
      FieldConfig.create({
        type: 'input',
        label: of(''),
        inputType: 'text',
        name: 'isDefault',
        listed: true,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message$: this.translocoService.selectTranslate('form.required')
          }
        ]
      }),
    ]
  };

  constructor(
    protected translocoService: TranslocoService,
    private query: InvoiceAddressQuery,
    private authService: AuthService,
    protected store: InvoiceAddressStore) {
    super(store, InvoiceAddress);
  }

  notifyAddressesChanged(invoice?: InvoiceAddress): void {
    if (invoice) {
      this.pendingCheckoutAddress = invoice;
    }
    this.addressListChanged.next();
  }

  getByUser(user: User): Observable<InvoiceAddress[]> {
    return this.fetchByUser(user).pipe(
      map(addresses => this.mergePendingCheckoutAddress(addresses))
    );
  }

  fetchByUser(user: User): Observable<InvoiceAddress[]> {
    if (!user?.id) {
      return of([]);
    }

    return this.getHttp().get<InvoiceAddress[]>(this.api + '/detail?q[]=user:eq:' + user.id)
      .pipe(
        map(items => {
          return (items ?? []).map(_item => new this._constructor(_item) as InvoiceAddress);
        }),
        take(1),
      );
  }

  mergePendingCheckoutAddress(addresses: InvoiceAddress[]): InvoiceAddress[] {
    const pending = this.pendingCheckoutAddress;
    if (!pending?.id) {
      return addresses;
    }

    if (addresses.some(address => address.id === pending.id)) {
      this.pendingCheckoutAddress = null;
      return addresses;
    }

    return [...addresses, pending];
  }

  get resourceName(): string {
    return 'webshop/invoice-address';
  }

}
